import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

export const PrivacyPolicy = () => {
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>PayFlow Privacy Policy - ProjectFlow Automations</title>
        <meta 
          name="description" 
          content="Official Privacy Policy for the PayFlow time tracking and paycheck estimation app, provided by ProjectFlow Automations." 
        />
        <meta property="og:title" content="PayFlow Privacy Policy" />
        <meta property="og:description" content="Official Privacy Policy for the PayFlow time tracking and paycheck estimation app, provided by ProjectFlow Automations." />
        <meta property="og:url" content="https://projectflowautomations.com/privacy-policy" />
      </Helmet>
      
      <div className="min-h-screen bg-[#0D1115] py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-['Montserrat'] font-bold text-4xl sm:text-5xl text-[#F5F7F8] mb-4">
            PRIVACY POLICY — PAYFLOW
          </h1>
          <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg">
            Last Updated: December 2025
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-[#1A2733]/50 backdrop-blur-sm border border-[#506273]/30 rounded-lg p-8 mb-8">
          <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg leading-relaxed mb-4">
            ProjectFlow Automations ("we," "our," or "us") operates the PayFlow mobile application and related services (the "App"). This Privacy Policy explains how we collect, use, store, and protect your information.
          </p>
          <p className="text-[#CBD3D9] font-['Open_Sans'] text-lg leading-relaxed">
            By using PayFlow, you agree to the practices described below.
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section className="bg-[#1A2733]/30 backdrop-blur-sm border border-[#506273]/20 rounded-lg p-8">
            <h2 className="font-['Montserrat'] font-bold text-2xl sm:text-3xl text-[#30D5C8] mb-6">
              1. Information We Collect
            </h2>
            <p className="text-[#CBD3D9] font-['Open_Sans'] text-base leading-relaxed mb-6">
              We collect the following types of information to support app functionality:
            </p>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-['Montserrat'] font-semibold text-xl text-[#F5F7F8] mb-4">
                  A. Information You Provide
                </h3>
                <ul className="space-y-2 text-[#CBD3D9] font-['Open_Sans']">
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Name</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Email address (for account access and support)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Work schedules and shift times</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Project names, pay rates, per diem, travel pay</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Tax profile selections</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Notes submitted with shifts</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-['Montserrat'] font-semibold text-xl text-[#F5F7F8] mb-4">
                  B. Automatically Collected Information
                </h3>
                <ul className="space-y-2 text-[#CBD3D9] font-['Open_Sans']">
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Device type and operating system</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>App usage and error logs</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Clock-in/clock-out timestamps</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>IP address (for security)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#30D5C8] mr-3">•</span>
                    <span>Notification interactions</span>
                  </li>
                </ul>
              </div>
            </div>

            <p className="text-[#F5F7F8] font-['Open_Sans'] font-semibold mt-6">
              We do not collect sensitive financial information or sell personal data.
            </p>
          </section>

          {/* Section 2 */}
          <section className="bg-[#1A2733]/30 backdrop-blur-sm border border-[#506273]/20 rounded-lg p-8">
            <h2 className="font-['Montserrat'] font-bold text-2xl sm:text-3xl text-[#30D5C8] mb-6">
              2. How We Use Your Information
            </h2>
            <p className="text-[#CBD3D9] font-['Open_Sans'] text-base leading-relaxed mb-4">
              We use your information to:
            </p>
            <ul className="space-y-2 text-[#CBD3D9] font-['Open_Sans'] mb-4">
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>Provide core PayFlow features (time tracking, pay estimation)</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>Calculate estimated wages and deductions</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>Display shift, history, and project summaries</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>Send reminders and alerts</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>Improve app performance</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>Provide customer support</span>
              </li>
            </ul>
            <p className="text-[#F5F7F8] font-['Open_Sans'] font-semibold">
              We do not use your data for advertising.
            </p>
          </section>

          {/* Section 3 */}
          <section className="bg-[#1A2733]/30 backdrop-blur-sm border border-[#506273]/20 rounded-lg p-8">
            <h2 className="font-['Montserrat'] font-bold text-2xl sm:text-3xl text-[#30D5C8] mb-6">
              3. Data Storage & Security
            </h2>
            <p className="text-[#CBD3D9] font-['Open_Sans'] text-base leading-relaxed mb-4">
              Your information is stored using industry-standard security methods. Data may be stored locally on your device and/or through secure backend services used by PayFlow.
            </p>
            <p className="text-[#CBD3D9] font-['Open_Sans'] text-base leading-relaxed">
              While we take strong measures to protect your information, no system is completely secure.
            </p>
          </section>

          {/* Section 4 */}
          <section className="bg-[#1A2733]/30 backdrop-blur-sm border border-[#506273]/20 rounded-lg p-8">
            <h2 className="font-['Montserrat'] font-bold text-2xl sm:text-3xl text-[#30D5C8] mb-6">
              4. Sharing of Information
            </h2>
            <p className="text-[#F5F7F8] font-['Open_Sans'] font-semibold mb-4">
              We do not sell or rent personal data.
            </p>
            <p className="text-[#CBD3D9] font-['Open_Sans'] text-base leading-relaxed mb-4">
              We only share limited information:
            </p>
            <ul className="space-y-2 text-[#CBD3D9] font-['Open_Sans']">
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>When required by law</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>To prevent fraud or abuse</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>With service providers supporting app functionality (hosting, error logging), who must protect your data</span>
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="bg-[#1A2733]/30 backdrop-blur-sm border border-[#506273]/20 rounded-lg p-8">
            <h2 className="font-['Montserrat'] font-bold text-2xl sm:text-3xl text-[#30D5C8] mb-6">
              5. Pay & Tax Disclaimer
            </h2>
            <p className="text-[#CBD3D9] font-['Open_Sans'] text-base leading-relaxed">
              PayFlow provides estimates only. Actual wages, deductions, and tax outcomes may differ from employer payroll calculations. Always verify your paycheck with your employer.
            </p>
          </section>

          {/* Section 6 */}
          <section className="bg-[#1A2733]/30 backdrop-blur-sm border border-[#506273]/20 rounded-lg p-8">
            <h2 className="font-['Montserrat'] font-bold text-2xl sm:text-3xl text-[#30D5C8] mb-6">
              6. Your Rights
            </h2>
            <p className="text-[#CBD3D9] font-['Open_Sans'] text-base leading-relaxed mb-4">
              Users may:
            </p>
            <ul className="space-y-2 text-[#CBD3D9] font-['Open_Sans']">
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>Request a copy of their data</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>Request correction or deletion</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>Disable notifications</span>
              </li>
              <li className="flex items-start">
                <span className="text-[#30D5C8] mr-3">•</span>
                <span>Delete the app to remove locally stored data</span>
              </li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="bg-[#1A2733]/30 backdrop-blur-sm border border-[#506273]/20 rounded-lg p-8">
            <h2 className="font-['Montserrat'] font-bold text-2xl sm:text-3xl text-[#30D5C8] mb-6">
              7. Children's Privacy
            </h2>
            <p className="text-[#CBD3D9] font-['Open_Sans'] text-base leading-relaxed">
              PayFlow is not intended for children under 13. We do not knowingly collect any information from children.
            </p>
          </section>

          {/* Section 8 */}
          <section className="bg-[#1A2733]/30 backdrop-blur-sm border border-[#506273]/20 rounded-lg p-8">
            <h2 className="font-['Montserrat'] font-bold text-2xl sm:text-3xl text-[#30D5C8] mb-6">
              8. Third-Party Services
            </h2>
            <p className="text-[#CBD3D9] font-['Open_Sans'] text-base leading-relaxed">
              PayFlow may use secure third-party tools for authentication, storage, analytics, or notifications. Their use is governed by their own privacy policies.
            </p>
          </section>

          {/* Section 9 */}
          <section className="bg-[#1A2733]/30 backdrop-blur-sm border border-[#506273]/20 rounded-lg p-8">
            <h2 className="font-['Montserrat'] font-bold text-2xl sm:text-3xl text-[#30D5C8] mb-6">
              9. Changes to this Policy
            </h2>
            <p className="text-[#CBD3D9] font-['Open_Sans'] text-base leading-relaxed">
              We may update this Privacy Policy from time to time. Updates will show a revised "Last Updated" date and may be posted within the app or on this page.
            </p>
          </section>

          {/* Section 10 */}
          <section className="bg-[#1A2733]/30 backdrop-blur-sm border border-[#506273]/20 rounded-lg p-8">
            <h2 className="font-['Montserrat'] font-bold text-2xl sm:text-3xl text-[#30D5C8] mb-6">
              10. Contact Information
            </h2>
            <p className="text-[#CBD3D9] font-['Open_Sans'] text-base leading-relaxed mb-4">
              If you have questions, contact us at:
            </p>
            <div className="bg-[#0D1115]/50 rounded-lg p-6 space-y-2">
              <p className="text-[#F5F7F8] font-['Montserrat'] font-semibold text-lg">
                ProjectFlow Automations
              </p>
              <p className="text-[#CBD3D9] font-['Open_Sans']">
                Email: <a href="mailto:support@projectflowautomations.com" className="text-[#30D5C8] hover:underline">support@projectflowautomations.com</a>
              </p>
              <p className="text-[#CBD3D9] font-['Open_Sans']">
                Website: <a href="https://www.projectflowautomations.com" className="text-[#30D5C8] hover:underline">www.projectflowautomations.com</a>
              </p>
            </div>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-12 text-center">
          <a
            href="/"
            className="inline-block bg-[#30D5C8] text-[#0D1115] hover:bg-[#30D5C8]/90 font-['Montserrat'] font-bold text-lg px-8 py-4 rounded-lg transition-colors"
          >
            Back to Home
          </a>
        </div>
        </div>
      </div>
    </>
  );
};
